((root, factory) => {
  const api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.CorkopiaStorefrontComplete = api;
  if (root?.document) {
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', () => api.init(root.document));
    else api.init(root.document);
  }
})(typeof window !== 'undefined' ? window : globalThis, (root) => {
  'use strict';

  const RECENT_KEY = 'corkopia-recent-products-v1';
  const INTEREST_KEY = 'corkopia-local-interest-v1';
  const SAVED_KEY = 'corkopia-saved-products-v1';
  const PREFERENCES_KEY = 'corkopia-local-preferences-v1';
  const MAX_RECENT = 8;
  const read = (key, fallback) => {
    try {
      const value = JSON.parse(root.localStorage?.getItem(key) || 'null');
      return value ?? fallback;
    } catch (_) {
      return fallback;
    }
  };
  const write = (key, value) => {
    try {
      root.localStorage?.setItem(key, JSON.stringify(value));
      return true;
    } catch (_) {
      return false;
    }
  };
  const emit = (name, detail = {}) => root.CorkopiaAnalytics?.emit?.(name, detail, root.document);
  const familyFromText = (value = '') => {
    const text = value.toLowerCase();
    if (/\bhalf[\s-]?rounds?\b/.test(text)) return 'half_round';
    if (/\brounds?\b|\bhollow\b/.test(text)) return 'cork_round';
    if (/\bflats?\b|\bthin\b/.test(text)) return 'cork_flat';
    if (/\bbranches?\b|\btwisted\b/.test(text)) return 'branch';
    if (/\bstatement\b|\bcenterpiece\b|\bfocal\b/.test(text)) return 'statement_piece';
    if (/\bbackground\b|\bcoverage\b/.test(text)) return 'background_piece';
    return '';
  };
  const normalizedFamily = (card) => card.dataset.normalizedFamily
    || ({ flat: 'cork_flat', round: 'cork_round', statement: 'statement_piece' }[card.dataset.corkType])
    || card.dataset.corkType
    || '';

  const catalogController = (scope) => {
    const grid = scope.querySelector('[data-cork-product-grid]');
    if (!grid) return null;
    // Empty-catalogue previews and optional demo fixtures carry the same
    // normalized family/size attributes as live cards, so the safe collection
    // controls can filter all three without granting either fixture commerce.
    const cards = () => [...grid.querySelectorAll('[data-cork-product-card], [data-demo-specimen], [data-placeholder-specimen]')];
    const search = scope.querySelector('[data-cork-search]');
    const maxPrice = scope.querySelector('[data-catalog-max-price]');
    const minLength = scope.querySelector('[data-catalog-min-length]');
    const role = scope.querySelector('[data-catalog-role]');
    const weight = scope.querySelector('[data-catalog-weight]');
    const availability = scope.querySelector('[data-catalog-availability]');
    const interpretation = scope.querySelector('[data-catalog-interpretation]');
    const count = scope.querySelector('[data-catalog-count]');
    const initialParams = new URLSearchParams(root.location.search);
    let activeFamily = initialParams.get('filter.category') || initialParams.get('filter') || 'all';
    let query = '';

    const activeSize = () => scope.querySelector('[data-catalog-size]:checked')?.value || '';
    const apply = ({ updateUrl = false, announce = true } = {}) => {
      const queryFamily = familyFromText(query);
      const underMatch = query.match(/\bunder\s*\$?\s*(\d+(?:\.\d+)?)\b/i);
      const queryMax = underMatch ? Number(underMatch[1]) * 100 : null;
      const explicitMax = Number(maxPrice?.value) > 0 ? Number(maxPrice.value) * 100 : null;
      const maximum = explicitMax || queryMax;
      const querySize = /\bsmall\b/i.test(query) ? 'small' : /\bmedium\b/i.test(query) ? 'medium' : /\blarge\b/i.test(query) ? 'large' : '';
      const size = activeSize() || querySize;
      const minimumLength = Number(minLength?.value) || 0;
      const roleValue = role?.value || '';
      const weightValue = weight?.value || '';
      const availabilityValue = availability?.value || '';
      const freeText = query
        .toLowerCase()
        .replace(/\b(under|small|medium|large|rounds?|flats?|branches?|half|statement|background|thin|twisted|centerpiece|focal)\b/g, ' ')
        .replace(/\$?\d+(?:\.\d+)?/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      let visible = 0;
      cards().forEach((card) => {
        const family = normalizedFamily(card);
        const familyMatch = (activeFamily === 'all' || (activeFamily === 'new' && card.dataset.newArrival === 'true') || family === activeFamily)
          && (!queryFamily || family === queryFamily);
        const sizeMatch = !size || card.dataset.normalizedSize === size || card.dataset.sizeClass === size;
        const priceMatch = !maximum || Number(card.dataset.price) <= maximum;
        const lengthMatch = !minimumLength || Number(card.dataset.lengthIn) >= minimumLength;
        const roleMatch = !roleValue || String(card.dataset.habitatRole || '').includes(roleValue);
        const textMatch = !freeText || String(card.dataset.search || '').includes(freeText);
        const ounces = Number(card.dataset.weightOz);
        const weightMatch = !weightValue || (weightValue === 'light' ? ounces < 16 : ounces >= 16);
        const availabilityMatch = !availabilityValue || card.dataset.availabilityState === availabilityValue;
        card.hidden = !(familyMatch && sizeMatch && priceMatch && lengthMatch && roleMatch && textMatch && weightMatch && availabilityMatch);
        if (!card.hidden) visible += 1;
      });
      scope.querySelectorAll('[data-cork-filter]').forEach((button) => button.classList.toggle('is-active', button.dataset.corkFilter === activeFamily));
      const empty = scope.querySelector('[data-filter-empty]');
      if (empty) empty.hidden = visible > 0;
      if (count) count.textContent = String(visible);
      if (interpretation) {
        const parts = [
          queryFamily ? `family: ${queryFamily.replaceAll('_', ' ')}` : '',
          size ? `size intent: ${size}` : '',
          maximum ? `maximum: $${(maximum / 100).toFixed(0)}` : '',
          minimumLength ? `minimum length: ${minimumLength} in` : '',
          roleValue ? `role: ${roleValue}` : ''
        ].filter(Boolean);
        interpretation.hidden = !query;
        interpretation.textContent = query
          ? `Read as ${parts.join(', ') || `the text “${query}”`}. Each listing's own measurements and availability are the final word.`
          : '';
      }
      if (updateUrl) {
        const url = new URL(root.location.href);
        activeFamily === 'all' ? url.searchParams.delete('filter') : url.searchParams.set('filter', activeFamily);
        root.history.replaceState({}, '', `${url.pathname}${url.search}#inventory`);
      }
      if (announce) {
        const live = scope.querySelector('[data-inventory-refresh-status]');
        if (live) live.textContent = `${visible} current ${visible === 1 ? 'piece' : 'pieces'} shown.`;
      }
      return visible;
    };

    scope.addEventListener('click', (event) => {
      const family = event.target.closest('[data-cork-filter]');
      if (family) {
        activeFamily = family.dataset.corkFilter;
        apply({ updateUrl: true });
        emit('collection_filter_used', { filter: activeFamily });
      }
      const clear = event.target.closest('[data-catalog-clear]');
      if (clear) {
        activeFamily = 'all';
        query = '';
        if (search) search.value = '';
        if (maxPrice) maxPrice.value = '';
        if (minLength) minLength.value = '';
        if (role) role.value = '';
        if (weight) weight.value = '';
        if (availability) availability.value = 'available';
        const any = scope.querySelector('[data-catalog-size][value=""]');
        if (any) any.checked = true;
        apply({ updateUrl: true });
      }
      const view = event.target.closest('[data-catalog-view]');
      if (view) {
        const mode = view.dataset.catalogView;
        scope.dataset.catalogLayout = mode;
        scope.querySelectorAll('[data-catalog-view]').forEach((button) => button.setAttribute('aria-pressed', String(button === view)));
        try { root.sessionStorage?.setItem('corkopia-catalog-view', mode); } catch (_) {}
      }
    });
    scope.querySelector('[data-catalog-search-form]')?.addEventListener('submit', (event) => {
      event.preventDefault();
      query = search?.value.trim() || '';
      apply();
      emit('natural_search_submitted', { surface: 'collection', has_query: Boolean(query) });
    });
    search?.addEventListener('input', () => { query = search.value.trim().toLowerCase(); apply({ announce: false }); });
    scope.querySelectorAll('[data-catalog-size], [data-catalog-role], [data-catalog-max-price], [data-catalog-min-length], [data-catalog-weight], [data-catalog-availability]').forEach((control) => control.addEventListener('change', () => apply()));
    scope.querySelector('[data-cork-sort]')?.addEventListener('change', (event) => {
      const mode = event.target.value;
      const sorted = cards().sort((a, b) => mode === 'price-descending'
        ? Number(b.dataset.price) - Number(a.dataset.price)
        : mode === 'price-ascending'
          ? Number(a.dataset.price) - Number(b.dataset.price)
          : mode === 'weight-ascending'
            ? Number(a.dataset.weightOz) - Number(b.dataset.weightOz)
            : mode === 'weight-descending'
              ? Number(b.dataset.weightOz) - Number(a.dataset.weightOz)
              : Number(a.dataset.position) - Number(b.dataset.position));
      const marker = grid.querySelector('.pagination, [data-filter-empty]');
      sorted.forEach((card) => grid.insertBefore(card, marker || null));
    });
    const storedView = root.sessionStorage?.getItem('corkopia-catalog-view');
    if (storedView === 'list') scope.querySelector('[data-catalog-view="list"]')?.click();
    apply({ announce: false });
    emit('collection_viewed', { collection: scope.dataset.inventorySurfaceId || '' });
    return { apply };
  };

  const recordRecent = (doc) => {
    const product = doc.querySelector('[data-cork-product-detail]');
    if (!product?.dataset.productUrl) return;
    const current = {
      url: product.dataset.productUrl,
      title: product.dataset.productTitle || 'Exact cork piece',
      pieceId: product.dataset.pieceId || '',
      family: product.dataset.family || '',
      image: product.dataset.productImage || '',
      viewedAt: new Date().toISOString()
    };
    const recent = read(RECENT_KEY, []).filter((entry) => entry?.url !== current.url);
    write(RECENT_KEY, [current, ...recent].slice(0, MAX_RECENT));
    emit('product_viewed', { piece_id: current.pieceId, family: current.family });
  };
  const renderRecent = (doc) => {
    const currentUrl = root.location?.pathname || '';
    const recent = read(RECENT_KEY, []).filter((entry) => entry?.url && !currentUrl.endsWith(entry.url)).slice(0, 6);
    doc.querySelectorAll('[data-recently-viewed]').forEach((section) => {
      const list = section.querySelector('[data-recently-viewed-list]');
      if (!list || !recent.length) return;
      list.replaceChildren(...recent.map((entry) => {
        const link = doc.createElement('a');
        link.href = entry.url;
        link.textContent = entry.pieceId ? `${entry.pieceId} · ${entry.title}` : entry.title;
        return link;
      }));
      section.hidden = false;
    });
  };
  const gallery = (doc) => {
    const galleryScope = doc.querySelector('[data-product-gallery]');
    if (!galleryScope) return;
    const stage = galleryScope.querySelector('.product-gallery__stage img');
    const zoom = doc.querySelector('[data-product-zoom]');
    galleryScope.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-product-thumbnail]');
      if (!thumb || !stage) return;
      stage.src = thumb.dataset.imageSrc;
      stage.alt = thumb.dataset.imageAlt;
      galleryScope.querySelectorAll('[data-product-thumbnail]').forEach((button) => button.setAttribute('aria-pressed', String(button === thumb)));
      const zoomImage = zoom?.querySelector('img');
      if (zoomImage) { zoomImage.src = thumb.dataset.imageSrc; zoomImage.alt = thumb.dataset.imageAlt; }
    });
    doc.querySelector('[data-product-zoom-open]')?.addEventListener('click', () => {
      zoom?.showModal();
      zoom?.querySelector('[data-product-zoom-close]')?.focus();
    });
    zoom?.querySelector('[data-product-zoom-close]')?.addEventListener('click', () => zoom.close());
    zoom?.addEventListener('click', (event) => { if (event.target === zoom) zoom.close(); });
    doc.querySelector('[data-product-sticky-add]')?.addEventListener('click', () => doc.querySelector('[data-cork-product-detail] [data-cork-add]')?.click());
  };
  const fitContext = (doc) => {
    const product = doc.querySelector('[data-cork-product-detail]');
    const copy = product?.querySelector('[data-product-fit-copy]');
    if (!product || !copy) return;
    const build = read('corkopia-build-studio-v1', {});
    const habitat = read('corkopia-habitat-studio-v1', {});
    const enclosure = habitat.enclosure?.internalDimensions || build.enclosure || {};
    const piece = [Number(product.dataset.length), Number(product.dataset.width), Number(product.dataset.height)].sort((a, b) => b - a);
    const space = [Number(enclosure.width), Number(enclosure.depth), Number(enclosure.height)].sort((a, b) => b - a);
    if (!piece.every(Number.isFinite) || !space.every((value) => Number.isFinite(value) && value > 0)) return;
    const fits = piece.every((value, index) => value <= space[index]);
    copy.textContent = fits
      ? `Its recorded maximum dimensions fit within your saved ${enclosure.width} × ${enclosure.depth} × ${enclosure.height} in envelope before protected clearances. Review orientation and the scaled canvas.`
      : `At least one recorded maximum extent exceeds your saved ${enclosure.width} × ${enclosure.depth} × ${enclosure.height} in envelope. Try another orientation only after checking doors and protected clearances.`;
  };
  const interestControls = (doc) => {
    doc.addEventListener('click', (event) => {
      const button = event.target.closest('[data-save-interest]');
      if (!button) return;
      const interests = read(INTEREST_KEY, []);
      const value = button.dataset.interest || 'catalog';
      if (!interests.includes(value)) interests.push(value);
      write(INTEREST_KEY, interests.slice(-20));
      button.textContent = 'Interest saved on this device ✓';
      button.disabled = true;
      emit('save_interest', { interest: value });
      emit('empty_state_interacted', { action: 'save_interest', interest: value });
    });
  };
  const accountHub = (doc) => {
    const hub = doc.querySelector('[data-account-hub]');
    if (!hub) return;
    const build = read('corkopia-build-studio-v1', null);
    const buildOutput = hub.querySelector('[data-account-build]');
    if (build && buildOutput) buildOutput.textContent = `A local ${build.project || 'custom'} build is available${build.updatedAt ? ` from ${new Date(build.updatedAt).toLocaleDateString()}` : ''}.`;
    const saved = read(SAVED_KEY, []);
    const savedOutput = hub.querySelector('[data-account-saved-pieces]');
    if (saved.length && savedOutput) {
      const list = doc.createElement('ul');
      saved.slice(-12).reverse().forEach((id) => { const item = doc.createElement('li'); item.textContent = `Piece ${id}`; list.append(item); });
      savedOutput.replaceChildren(list);
    }
    const recent = read(RECENT_KEY, []);
    const recentOutput = hub.querySelector('[data-account-recent]');
    if (recent.length && recentOutput) {
      const list = doc.createElement('ul');
      recent.slice(0, 8).forEach((entry) => { const item = doc.createElement('li'); const link = doc.createElement('a'); link.href = entry.url; link.textContent = entry.pieceId || entry.title; item.append(link); list.append(item); });
      recentOutput.replaceChildren(list);
    }
    const memory = read('corkopia-store-memory-v1', {});
    const projectsOutput = hub.querySelector('[data-account-projects]');
    if (Array.isArray(memory.recentProjects) && memory.recentProjects.length && projectsOutput) {
      const list = doc.createElement('ul');
      memory.recentProjects.slice(-6).reverse().forEach((entry) => { const item = doc.createElement('li'); item.textContent = entry.label || entry.project || 'Local project'; list.append(item); });
      projectsOutput.replaceChildren(list);
    }
    const preferences = read(PREFERENCES_KEY, {});
    hub.querySelectorAll('[data-local-preference]').forEach((input) => {
      input.checked = preferences[input.dataset.localPreference] === true;
      input.addEventListener('change', () => {
        const next = read(PREFERENCES_KEY, {});
        next[input.dataset.localPreference] = input.checked;
        write(PREFERENCES_KEY, next);
      });
    });
  };
  const analyticsBindings = (doc) => {
    doc.querySelectorAll('[data-build-entry]').forEach((button) => button.addEventListener('click', () => emit('build_started', { entry: button.dataset.buildEntry || '' }), { once: true }));
    doc.addEventListener('corkopia:habitat-build-accepted', (event) => emit('build_accepted', {
      source: 'habitat_intelligence',
      cart_mutation: event.detail?.cartMutation === true
    }));
    doc.querySelectorAll('.prebuilt-product-form').forEach((form) => form.addEventListener('submit', () => emit('prebuilt_box_added', { phase: 'submitted' })));
    doc.addEventListener('corkopia:piece-added', (event) => emit('product_added_to_cart', {
      piece_id: event.detail?.pieceId || '',
      variant_id: event.detail?.variantId || ''
    }));
    doc.addEventListener('corkopia:smart-build', (event) => {
      if (event.detail?.smart_build_action === 'automatic_box_upgraded' || event.detail?.smart_build_action === 'automatic_box_downgraded') {
        emit('box_size_changed', { action: event.detail.smart_build_action, shipping_box: event.detail.shipping_box || '' });
      }
    });
    const mode = new URLSearchParams(root.location?.search || '').get('mode');
    doc.querySelector('[data-search-form]')?.addEventListener('submit', () => emit(mode === 'standard' ? 'standard_search_submitted' : 'natural_search_submitted', { surface: 'search' }));
  };

  let initialized = false;
  const init = (doc) => {
    if (initialized || !doc) return false;
    initialized = true;
    doc.querySelectorAll('[data-catalog]').forEach((scope) => { scope.corkopiaCatalog = catalogController(scope); });
    recordRecent(doc);
    renderRecent(doc);
    gallery(doc);
    fitContext(doc);
    interestControls(doc);
    accountHub(doc);
    analyticsBindings(doc);
    doc.addEventListener('corkopia:products-ready', () => doc.querySelectorAll('[data-catalog]').forEach((scope) => scope.corkopiaCatalog?.apply?.({ announce: false })));
    return true;
  };
  return Object.freeze({ INTEREST_KEY, MAX_RECENT, PREFERENCES_KEY, RECENT_KEY, SAVED_KEY, catalogController, familyFromText, init, read, write });
});
